import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, ExternalLink, Filter } from 'lucide-react';

interface Application {
  id: string;
  name: string;
  description: string;
  category: string;
  status: 'active' | 'maintenance' | 'development';
  url: string;
  icon: string;
}

const applications: Application[] = [
  {
    id: '1',
    name: 'Firemné Masáže',
    description: 'Systém pre objednávanie a správu firemných masáží. Umožňuje zamestnancom rezervovať si termíny masáží priamo v práci.',
    category: 'Wellness',
    status: 'active',
    url: 'https://masaze.iacslovakia.sk/',
    icon: '💆‍♂️'
  },
  {
    id: '6',
    name: 'Task Management',
    description: 'Prehľadový dashboard pre riadenie projektov a úloh.',
    category: 'Project Management',
    status: 'active',
    url: 'https://ulohy.iacslovakia.sk/',
    icon: '📊'
  },
  {
    id: '8',
    name: 'Training Portal',
    description: 'Online vzdelávací portál s kurzami a certifikátmi pre zamestnancov.',
    category: 'Education',
    status: 'development',
    url: '/apps/training',
    icon: '🎓'
  },
  {
    id: '11',
    name: 'Firemný Koučing',
    description: 'Systém pre objednávanie a správu firemných koučingových služieb. Umožňuje zamestnancom rezervovať si koučingové sedenia.',
    category: 'Wellness',
    status: 'active',
    url: 'https://koucing.iacslovakia.sk/',
    icon: '🎯'
  },
  {
    id: '12',
    name: 'Manažér Výročí',
    description: 'Systém pre správu a pripomínanie výročí zamestnancov. Umožňuje sledovať dôležité dátumy a oslavy.',
    category: 'HR',
    status: 'active',
    url: 'https://vyrocia.iacslovakia.sk/',
    icon: '🎉'
  },
  {
    id: '13',
    name: 'Home Office Portal',
    description: 'Portál pre správu a žiadosti o prácu z domu. Umožňuje zamestnancom žiadať a spravovať home office dní.',
    category: 'HR',
    status: 'active',
    url: 'https://homeoffice.iacslovakia.sk/',
    icon: '🏠'
  },
  {
    id: '14',
    name: 'Recruiting Portal',
    description: 'Náborový systém pre Indirect a salaried pozície. Správa kandidátov, náborových žiadostí, schvaľovacích procesov a štatistík.',
    category: 'HR',
    status: 'active',
    url: 'https://recruiting.iacslovakia.sk/',
    icon: '👔'
  },
  {
    id: '15',
    name: 'Skill Matrix',
    description: 'Systém pre správu a mapovanie zručností zamestnancov. Umožňuje sledovať kompetencie, certifikáty a rozvojové potreby.',
    category: 'HR',
    status: 'development',
    url: 'https://skillmatrix.iacslovakia.sk/',
    icon: '📈'
  },
  {
    id: '16',
    name: 'Nábor - Operátori',
    description: 'Náborový systém pre operátorov výroby a operátorov logistiky. Umožňuje správu kandidátov a náborových procesov pre výrobné pozície.',
    category: 'HR',
    status: 'active',
    url: 'https://nabor.iacslovakia.sk/',
    icon: '🏭'
  },
  {
    id: '17',
    name: 'Hodnotenie Dodávateľov',
    description: 'Systém pre hodnotenie a správu dodávateľov. Umožňuje hodnotiť výkonnosť, kvalitu a spokojnosť s dodávateľmi.',
    category: 'Procurement',
    status: 'active',
    url: 'https://dodavatelia.iacslovakia.sk/',
    icon: '📦'
  },
  {
    id: '18',
    name: 'Správa Obrazoviek',
    description: 'Systém pre pridávanie a správu obsahu na firemné obrazovky. Umožňuje vytvárať a spravovať obsah pre digitálne displeje.',
    category: 'Communication',
    status: 'active',
    url: 'https://obrazovky.iacslovakia.sk/',
    icon: '📺'
  },
  {
    id: '19',
    name: 'Dotazníky',
    description: 'Systém pre vytváranie a správu dotazníkov. Umožňuje vytvárať prieskumy, zberať odpovede a analyzovať výsledky.',
    category: 'Communication',
    status: 'active',
    url: 'https://dotaznik.iacslovakia.sk/',
    icon: '📝'
  },
  {
    id: '20',
    name: '360° Hodnotenie',
    description: 'Systém pre komplexné 360 stupňové hodnotenie zamestnancov. Umožňuje zberať spätnú väzbu od kolegov, nadriadených a podriadených.',
    category: 'HR',
    status: 'active',
    url: 'https://360.iacslovakia.sk/',
    icon: '🔄'
  },
  {
    id: '21',
    name: 'LinkedIn Post Generator',
    description: 'Systém pre generovanie a vytváranie LinkedIn postov. Umožňuje vytvárať profesionálny obsah pre firemné LinkedIn účty.',
    category: 'Communication',
    status: 'active',
    url: 'https://linkedin.iacslovakia.sk/',
    icon: '💼'
  },
  {
    id: '22',
    name: 'Agency Management',
    description: 'Systém pre správu zamestnancov agentúr. Umožňuje správu zamestnancov, dokumentov, štatistík a filtrovanie podľa agentúr, pozícií a kategórií.',
    category: 'HR',
    status: 'active',
    url: 'https://agentury.iacslovakia.sk/',
    icon: '🏢'
  }
];

const categories = ['Všetky', 'Wellness', 'Project Management', 'Education', 'HR', 'Procurement', 'Communication'];

export default function Applications() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Všetky');
  const [filteredApps, setFilteredApps] = useState(applications);

  useEffect(() => {
    let filtered = applications;

    if (selectedCategory !== 'Všetky') {
      filtered = filtered.filter(app => app.category === selectedCategory);
    }

    if (searchTerm) {
      filtered = filtered.filter(app =>
        app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredApps(filtered);
  }, [searchTerm, selectedCategory]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'development': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Aktívna';
      case 'maintenance': return 'Údržba';
      case 'development': return 'Vývoj';
      default: return 'Neznámy';
    }
  };

  const handleAppClick = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-slate-800 mb-2">
            Naše Aplikácie
          </h1>
          <p className="text-slate-600">
            Objavte naše interné aplikácie a nástroje pre zamestnancov
          </p>
        </motion.div>

        {/* Vyhľadávanie a filtre */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-8 space-y-4"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Hľadať aplikácie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full bg-white border-slate-300 text-slate-900 placeholder-slate-500 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="text-gray-400 w-5 h-5" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="text-sm text-slate-600">
            Nájdených aplikácií: {filteredApps.length}
          </div>
        </motion.div>

        {/* Mriežka aplikácií */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          <AnimatePresence>
            {filteredApps.map((app, index) => (
              <motion.div
                key={app.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="cursor-pointer"
              >
                <Card className={`h-full hover:shadow-xl transition-all duration-300 bg-white border-slate-200 shadow-sm relative ${app.status !== 'active' ? 'opacity-75' : ''}`}>
                  {app.status !== 'active' && (
                    <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                      <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg px-6 py-3 shadow-lg transform rotate-[-5deg]">
                        <span className="text-yellow-800 font-bold text-lg tracking-wider">
                          {app.status === 'development' ? 'VO VÝVOJI' : 'ÚDRŽBA'}
                        </span>
                      </div>
                    </div>
                  )}
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-3xl">{app.icon}</div>
                        <div>
                          <CardTitle className={`text-lg font-semibold ${app.status !== 'active' ? 'text-slate-400' : 'text-slate-800'}`}>
                            {app.name}
                          </CardTitle>
                          <Badge variant="secondary" className="text-xs mt-1 bg-slate-100 text-slate-700">
                            {app.category}
                          </Badge>
                        </div>
                      </div>
                      <Badge className={`text-xs ${getStatusColor(app.status)}`}>
                        {getStatusText(app.status)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <CardDescription className={`text-sm line-clamp-3 ${app.status !== 'active' ? 'text-slate-400' : 'text-slate-600'}`}>
                      {app.description}
                    </CardDescription>

                    <Button
                      onClick={() => handleAppClick(app.url)}
                      className="w-full group bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 shadow-sm hover:shadow-md"
                      disabled={app.status !== 'active'}
                    >
                      {app.status === 'development' ? 'Vo vývoji' : app.status === 'maintenance' ? 'Údržba' : 'Otvoriť aplikáciu'}
                      <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Prázdny stav */}
        {filteredApps.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="text-slate-400 mb-4">
              <Search className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-slate-800 mb-2">
              Žiadne aplikácie neboli nájdené
            </h3>
            <p className="text-slate-600">
              Skúste upraviť svoje vyhľadávacie kritériá
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}